import React, { useEffect, useState } from 'react';
import Card from '../components/Card';
import Button from '../components/Button';
import FormField, { inputStyle } from '../components/FormField';
import TabList from '../components/TabList';
import * as configApi from '../services/configApi';
import * as refPartyTelegramApi from '../services/refPartyTelegramApi';
import { formatDateTime } from '../utils/dateFormat';

const TABS = ['Company', 'Locations', 'Low Stock', 'Push Notifications', 'Backup', 'Search'];
const TAB_ITEMS = TABS.map((t) => ({ key: t, label: t }));

function TabBar({ active, onChange }) {
  return <TabList tabs={TAB_ITEMS} activeKey={active} onChange={onChange} ariaLabel="Settings sections" />;
}

function CompanyTab() {
  const [company, setCompany] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    configApi.getCompany().then(setCompany);
  }, []);

  if (!company) return <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const updated = await configApi.updateCompanySetup({
        name: company.name,
        legal_name: company.legal_name,
        gstin: company.gstin,
        address: company.address,
        city: company.city,
        state: company.state,
        country: company.country,
      });
      setCompany(updated);
      setMessage('Company details saved.');
    } catch (err) {
      setMessage(err.message || 'Could not save company details.');
    } finally {
      setSaving(false);
    }
  };

  const field = (key) => ({
    value: company[key] || '',
    onChange: (e) => setCompany({ ...company, [key]: e.target.value }),
  });

  return (
    <Card title="Company Setup">
      <form onSubmit={handleSubmit}>
        {message && (
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
            {message}
          </p>
        )}
        <div className="responsive-grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
          <FormField label="Company name">
            <input required style={inputStyle} {...field('name')} />
          </FormField>
          <FormField label="Legal name">
            <input style={inputStyle} {...field('legal_name')} />
          </FormField>
          <FormField label="GSTIN">
            <input style={inputStyle} {...field('gstin')} />
          </FormField>
          <FormField label="Address">
            <input style={inputStyle} {...field('address')} />
          </FormField>
          <FormField label="City">
            <input style={inputStyle} {...field('city')} />
          </FormField>
          <FormField label="State">
            <input style={inputStyle} {...field('state')} />
          </FormField>
        </div>
        <Button type="submit" disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </form>
    </Card>
  );
}

function LocationsTab() {
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({ name: '', address: '' });
  const [error, setError] = useState(null);

  const load = () => configApi.listLocations().then(setLocations);

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await configApi.createLocation(form);
      setForm({ name: '', address: '' });
      await load();
    } catch (err) {
      setError(err.message || 'Could not add location.');
    }
  };

  const handleRemove = async (locationId) => {
    await configApi.deleteLocation(locationId);
    await load();
  };

  return (
    <Card title="Location Management">
      <form
        onSubmit={handleAdd}
        className="responsive-flex-form"
        style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end', marginBottom: 'var(--space-4)' }}
      >
        <FormField label="Location name" >
          <input
            required
            style={inputStyle}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </FormField>
        <FormField label="Address">
          <input
            style={inputStyle}
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
          />
        </FormField>
        <Button type="submit" style={{ marginBottom: 'var(--space-4)' }}>
          Add
        </Button>
      </form>
      {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {locations.map((loc) => (
          <li
            key={loc.location_id}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '10px 0',
              borderBottom: '1px solid var(--color-border)',
            }}
          >
            <div>
              <div>{loc.name}</div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                {loc.address}
              </div>
            </div>
            <Button variant="text" onClick={() => handleRemove(loc.location_id)}>
              Remove
            </Button>
          </li>
        ))}
        {locations.length === 0 && (
          <li style={{ color: 'var(--color-text-muted)', padding: '10px 0' }}>
            No locations yet — add your first warehouse or branch above.
          </li>
        )}
      </ul>
    </Card>
  );
}

function LowStockTab() {
  const [settings, setSettings] = useState([]);
  const [threshold, setThreshold] = useState('');
  const [error, setError] = useState(null);

  const load = () => configApi.listLowStockSettings().then(setSettings);

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await configApi.createLowStockSetting({
        categoryId: null,
        designId: null,
        thresholdQty: Number(threshold),
      });
      setThreshold('');
      await load();
    } catch (err) {
      setError(err.message || 'Could not save threshold.');
    }
  };

  return (
    <Card title="Low Stock Settings">
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
        Set a company-wide low-stock threshold. Per-category and per-design thresholds are
        available once Categories/Designs exist (Step 4 onward).
      </p>
      <form onSubmit={handleAdd} className="responsive-flex-form" style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end' }}>
        <FormField label="Threshold quantity">
          <input
            type="number"
            min="0"
            required
            style={inputStyle}
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
          />
        </FormField>
        <Button type="submit" style={{ marginBottom: 'var(--space-4)' }}>
          Add threshold
        </Button>
      </form>
      {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {settings.map((s) => (
          <li
            key={s.setting_id}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '10px 0',
              borderBottom: '1px solid var(--color-border)',
            }}
          >
            <span>
              {s.category_name || s.design_name || 'Company-wide'}: threshold{' '}
              <strong className="data-figure">{s.threshold_qty}</strong>
            </span>
          </li>
        ))}
        {settings.length === 0 && (
          <li style={{ color: 'var(--color-text-muted)', padding: '10px 0' }}>
            No thresholds configured yet.
          </li>
        )}
      </ul>
    </Card>
  );
}

function BackupTab() {
  const [backups, setBackups] = useState([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const load = () => configApi.listBackups().then(setBackups);

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      await configApi.createBackup();
      await load();
    } catch (err) {
      setError(err.message || 'Backup failed.');
    } finally {
      setCreating(false);
    }
  };

  const handleRestore = async (backupId) => {
    if (!window.confirm('Restoring will overwrite current data with this backup. Continue?')) {
      return;
    }
    try {
      await configApi.restoreBackup(backupId);
      await load();
    } catch (err) {
      setError(err.message || 'Restore failed.');
    }
  };

  return (
    <Card
      title="Backup & Restore"
      actions={
        <Button onClick={handleCreate} disabled={creating}>
          {creating ? 'Backing up…' : 'Create backup'}
        </Button>
      }
    >
      {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {backups.map((b) => (
          <li
            key={b.backup_id}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 0',
              borderBottom: '1px solid var(--color-border)',
            }}
          >
            <div>
              <div className="data-figure" style={{ fontSize: 'var(--font-size-sm)' }}>
                {formatDateTime(b.created_at)}
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                {b.status} {b.size_bytes ? `· ${(b.size_bytes / 1024).toFixed(1)} KB` : ''}
              </div>
            </div>
            {b.status === 'completed' && (
              <Button variant="text" onClick={() => handleRestore(b.backup_id)}>
                Restore
              </Button>
            )}
          </li>
        ))}
        {backups.length === 0 && (
          <li style={{ color: 'var(--color-text-muted)', padding: '10px 0' }}>
            No backups yet.
          </li>
        )}
      </ul>
    </Card>
  );
}

function SearchTab() {
  const [config, setConfig] = useState([]);

  const load = () => configApi.listSearchConfig().then(setConfig);

  useEffect(() => {
    load();
  }, []);

  const toggle = async (item) => {
    await configApi.updateSearchConfig(item.moduleId, {
      isEnabled: !item.isEnabled,
      searchWeight: item.searchWeight,
    });
    await load();
  };

  const updateWeight = async (item, weight) => {
    setConfig((prev) => prev.map((c) => (c.moduleId === item.moduleId ? { ...c, searchWeight: weight } : c)));
    await configApi.updateSearchConfig(item.moduleId, { isEnabled: item.isEnabled, searchWeight: weight });
  };

  return (
    <Card title="Global Search Configuration">
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
        Control which modules are searchable from the Global Search bar, and how heavily each
        one is weighted — a higher weight means that module's results rank above equally
        good matches from lower-weighted modules.
      </p>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {config.map((item) => (
          <li
            key={item.moduleId}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 0',
              borderBottom: '1px solid var(--color-border)',
              gap: 'var(--space-3)',
            }}
          >
            <span>{item.moduleName}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                Weight
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={item.searchWeight}
                  disabled={!item.isEnabled}
                  onChange={(e) => updateWeight(item, Number(e.target.value))}
                  style={{ ...inputStyle, width: 56, padding: '4px 8px' }}
                />
              </label>
              <Button variant="text" onClick={() => toggle(item)}>
                {item.isEnabled ? 'Enabled — click to disable' : 'Disabled — click to enable'}
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * Settings > Push Notifications: register a ref. party's Telegram chat id.
 * Once set, that ref. party automatically gets a Telegram message the
 * moment a customer's WHOLE order becomes fully dispatched (see
 * orderAnalysisService.notifyNewlyCompletedCustomers on the backend) — no
 * action needed here beyond keeping the chat id up to date. A ref. party
 * can be registered here even before any order has ever mentioned them;
 * if one later comes in from an Order Excel with a matching name, it
 * reuses this same record (and its chat id) rather than creating a
 * second, unconfigured one.
 */
function PushNotificationsTab() {
  const [settings, setSettings] = useState([]);
  const [name, setName] = useState('');
  const [chatId, setChatId] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => refPartyTelegramApi.listRefPartyTelegramSettings().then(setSettings);

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      await refPartyTelegramApi.upsertRefPartyTelegramSetting(name, chatId);
      setName('');
      setChatId('');
      await load();
    } catch (err) {
      setError(err.message || 'Could not save this ref. party.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (s) => {
    setName(s.name);
    setChatId(s.telegram_chat_id || '');
  };

  const handleRemove = async (refPartyId) => {
    await refPartyTelegramApi.removeRefPartyTelegramSetting(refPartyId);
    await load();
  };

  const [notifyResult, setNotifyResult] = useState(null);
  const [notifyingId, setNotifyingId] = useState(null);

  const handleNotifyNow = async (refPartyId) => {
    setNotifyingId(refPartyId);
    setNotifyResult(null);
    try {
      const result = await refPartyTelegramApi.notifyNowRefParty(refPartyId);
      setNotifyResult({ refPartyId, ...result });
    } catch (err) {
      setNotifyResult({ refPartyId, error: err.message || 'Could not send.' });
    } finally {
      setNotifyingId(null);
    }
  };

  const handleToggleSalesReport = async (refPartyId, currentlyEnabled) => {
    // Optimistic update — the checkbox flips immediately, then confirms
    // against the server; on failure it's reverted by the next load().
    setSettings((prev) => prev.map((s) => (s.ref_party_id === refPartyId ? { ...s, sales_report_enabled: currentlyEnabled ? 0 : 1 } : s)));
    try {
      await refPartyTelegramApi.setSalesReportEnabled(refPartyId, !currentlyEnabled);
    } catch (err) {
      await load();
    }
  };

  const handleToggleReorderReport = async (refPartyId, currentlyEnabled) => {
    setSettings((prev) => prev.map((s) => (s.ref_party_id === refPartyId ? { ...s, reorder_report_enabled: currentlyEnabled ? 0 : 1 } : s)));
    try {
      await refPartyTelegramApi.setReorderReportEnabled(refPartyId, !currentlyEnabled);
    } catch (err) {
      await load();
    }
  };

  const handleToggleInactiveDigest = async (refPartyId, currentlyEnabled) => {
    setSettings((prev) => prev.map((s) => (s.ref_party_id === refPartyId ? { ...s, inactive_customer_digest_enabled: currentlyEnabled ? 0 : 1 } : s)));
    try {
      await refPartyTelegramApi.setInactiveDigestEnabled(refPartyId, !currentlyEnabled);
    } catch (err) {
      await load();
    }
  };

  // DAILY-UI v1
  const handleToggleDailyAction = async (refPartyId, currentlyEnabled) => {
    setSettings((prev) => prev.map((s) => (s.ref_party_id === refPartyId ? { ...s, daily_action_enabled: currentlyEnabled ? 0 : 1 } : s)));
    try {
      await refPartyTelegramApi.setDailyActionEnabled(refPartyId, !currentlyEnabled);
    } catch (err) {
      await load();
    }
  };

  const handleDailyActionTest = async (refPartyId, name) => {
    if (!window.confirm(`Send today's action list to ${name} on Telegram now?`)) return;
    setNotifyingId(refPartyId);
    try {
      const r = await refPartyTelegramApi.dailyActionTest(refPartyId);
      let info = 'Nothing to send today (no customers to follow up and no target set).';
      if (r.sentCount > 0) info = `Today's action list sent (${r.sentCount} message).`;
      else if (r.failed > 0) info = 'Telegram sending failed — please check the server log.';
      setNotifyResult({ refPartyId, info });
    } catch (err) {
      setNotifyResult({ refPartyId, error: 'Could not send the action list.' });
    } finally {
      setNotifyingId(null);
    }
  };

  return (
    <Card title="Push Notifications">
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
        Add a Ref. Party's Telegram chat id here. The moment a customer's whole order — every
        design they ordered — becomes fully dispatched, that ref. party gets an automatic
        Telegram message telling them the order is ready, so they know to arrange material
        dispatch. Message a Telegram bot to find a chat id before entering it here.
      </p>
      <form onSubmit={handleAdd} className="responsive-flex-form" style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <FormField label="Ref. Party name">
          <input
            type="text"
            required
            style={inputStyle}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. AgentRaj"
          />
        </FormField>
        <FormField label="Telegram chat id">
          <input
            type="text"
            required
            style={inputStyle}
            value={chatId}
            onChange={(e) => setChatId(e.target.value)}
            placeholder="e.g. 123456789"
          />
        </FormField>
        <Button type="submit" disabled={saving} style={{ marginBottom: 'var(--space-4)' }}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </form>
      {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {settings.map((s) => (
          <li
            key={s.ref_party_id}
            style={{
              padding: '10px 0',
              borderBottom: '1px solid var(--color-border)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>
                <strong>{s.name}</strong>
                {' — '}
                {s.telegram_chat_id ? (
                  <span className="data-figure" style={{ color: 'var(--color-success)' }}>{s.telegram_chat_id}</span>
                ) : (
                  <span style={{ color: 'var(--color-text-muted)' }}>not configured</span>
                )}
              </span>
              <span style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <Button variant="text" onClick={() => handleEdit(s)}>Edit</Button>
                {s.telegram_chat_id && (
                  <>
                    <Button
                      variant="text"
                      disabled={notifyingId === s.ref_party_id}
                      onClick={() => handleNotifyNow(s.ref_party_id)}
                    >
                      {notifyingId === s.ref_party_id ? 'Sending…' : 'Notify Now'}
                    </Button>
                    <Button variant="text" onClick={() => handleRemove(s.ref_party_id)}>Remove</Button>
                  </>
                )}
              </span>
            </div>
            {s.telegram_chat_id && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!s.sales_report_enabled}
                  onChange={() => handleToggleSalesReport(s.ref_party_id, !!s.sales_report_enabled)}
                />
                Also send weekly &amp; monthly size-wise sales report
              </label>
            )}
            {s.telegram_chat_id && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!s.reorder_report_enabled}
                  onChange={() => handleToggleReorderReport(s.ref_party_id, !!s.reorder_report_enabled)}
                />
                Also send weekly reorder watchlist (predicted next-15-day customer orders)
              </label>
            )}
            {s.telegram_chat_id && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!s.inactive_customer_digest_enabled}
                  onChange={() => handleToggleInactiveDigest(s.ref_party_id, !!s.inactive_customer_digest_enabled)}
                />
                Also send 90+/180+/1 Year+ inactive customer digest (every 15 days)
              </label>
            )}
            {s.telegram_chat_id && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!s.daily_action_enabled}
                    onChange={() => handleToggleDailyAction(s.ref_party_id, !!s.daily_action_enabled)}
                  />
                  Send daily &quot;Today&apos;s action list&quot; every morning 9:45 (target, overdue re-orders, due this week, win-back)
                </label>
                <Button
                  variant="text"
                  disabled={notifyingId === s.ref_party_id}
                  onClick={() => handleDailyActionTest(s.ref_party_id, s.name)}
                >
                  Send now
                </Button>
              </div>
            )}
            {notifyResult && notifyResult.refPartyId === s.ref_party_id && (
              <p style={{ margin: '6px 0 0', fontSize: 'var(--font-size-xs)', color: notifyResult.error ? 'var(--color-danger)' : 'var(--color-text-secondary)' }}>
                {notifyResult.error
                  ? notifyResult.error
                  : notifyResult.info
                    ? notifyResult.info
                  : `Found ${notifyResult.customersFound} customer(s) with a completed order — sent ${notifyResult.sentCount}.`}
              </p>
            )}
          </li>
        ))}
        {settings.length === 0 && (
          <p style={{ color: 'var(--color-text-muted)' }}>No ref. parties yet — add one above.</p>
        )}
      </ul>
    </Card>
  );
}

export default function SettingsPage() {
  const [tab, setTab] = useState('Company');

  return (
    <div>
      <h1>System Configuration</h1>
      <TabBar active={tab} onChange={setTab} />
      {tab === 'Company' && <CompanyTab />}
      {tab === 'Locations' && <LocationsTab />}
      {tab === 'Low Stock' && <LowStockTab />}
      {tab === 'Push Notifications' && <PushNotificationsTab />}
      {tab === 'Backup' && <BackupTab />}
      {tab === 'Search' && <SearchTab />}
    </div>
  );
}

// DAILY-UI v1
